import React, { useMemo } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import {
  CertificationValues,
  operationalEmissionsStore,
} from "../OperationalEmissionsStore";
import clsx from "clsx";

interface Info {
  row: { original: { [key: string]: string | number }; index: number };
}

const excludedColumns = ["value_per_m2_year", "unit"];

const useModulesTableColumns = (certName: string) => {
  const { t } = useTranslation();
  const currentData = operationalEmissionsStore.certificationsData?.[
    certName
  ] as CertificationValues;
  const columnNames = Object.keys(currentData.columns[2]).filter(
    (item) => !excludedColumns.includes(item)
  );

  function getUnit(unit?: string | null | number) {
    if (certName.includes("18")) {
      return (
        <span>
          {` ${unit}`}/m
          <sup>2</sup>
          <sub>DGNB</sub>*a
        </span>
      );
    } else if (certName.includes("BNB")) {
      return (
        <span>
          {` ${unit}`}/m
          <sup>2</sup>
          <sub>NGF</sub>
        </span>
      );
    } else {
      return (
        <span>
          {` ${unit}`}/m
          <sup>2</sup>
          <sub>NRF</sub>*a
        </span>
      );
    }
  }

  function getValueCell(key: string, info?: Info) {
    const currentItemValue = Object.values(currentData.values)
      .find((rec) => rec.friendly_name === info?.row.original.friendly_name)
      ?.indicator_values?.find((rec) => rec.name === key);

    const isB6 = info?.row.original.friendly_name === "B6";

    return (
      <div>
        <div
          className={clsx(
            "text-gray-900 font-medium whitespace-nowrap",
            isB6 ? "font-semibold" : ""
          )}
        >
          {currentItemValue?.value_per_m2_year != null
            ? currentItemValue?.value_per_m2_year
            : "-"}
          {currentItemValue?.value_per_m2_year != null
            ? getUnit(currentItemValue?.unit)
            : ""}
        </div>
        <div className="text-gray-500 font-normal whitespace-nowrap">
          {currentItemValue?.value != null ? currentItemValue?.value : "-"}
          {currentItemValue?.value != null ? ` ${currentItemValue?.unit}` : ""}
        </div>
      </div>
    );
  }

  function getColumn(key: string) {
    if (key === "description" || key === "friendly_name") {
      return {
        accessorFn: (row: { [key: string]: string }) => row[key],
        id: `${key}`,
        cell: (info: Info) => {
          const isB6 = info.row.original.friendly_name === "B6";
          return (
            <div
              className={clsx(
                isB6 ? "font-semibold" : "",
                "text-gray-900 whitespace-nowrap"
              )}
            >
              {isB6 && key === "friendly_name"
                ? t("operationalEnergy.sumB6")
                : info.row.original[key]}
            </div>
          );
        },
        header: () => (
          <div>
            {key === "friendly_name" ? t("operationalEnergy.module") : "Name"}
          </div>
        ),
        enableColumnFilter: false,
        enableSorting: false,
        minWidth: "auto",
        maxWidth: "auto",
      };
    }

    return {
      accessorFn: (row: { [key: string]: string }) => row[key],
      id: `${key}`,
      cell: (info: Info) => getValueCell(key, info),
      header: () => (
        <div>{key === "module_name" ? t("operationalEnergy.module") : key}</div>
      ),
      enableColumnFilter: false,
      enableSorting: false,
      minWidth: "auto",
      maxWidth: "auto",
    };
  }

  return useMemo<ColumnDef<{ [key: string]: string }>[]>(
    () => [...columnNames.map((key) => getColumn(key))],
    []
  );
};

export default useModulesTableColumns;
